import secureApi from "../../utils/secureApi";

// Existing admin dropdown API used elsewhere in the app.
export const getAcademyDropdown = async () => {
  try {
    const response: any = await secureApi.get(
      "/api/v1/academy/admin/dropdown"
    );
    // Backwards-compatible: preserve previous behavior of returning response.data
    return response?.data || [];
  } catch (error) {
    console.error("Failed to fetch academy dropdown:", error);
    return [];
  }
};

// =========================
// Generic academy list for scope IDs (Access Management)
// =========================

export interface AcademyBasicDetails {
  academyName: string;
}

export interface AcademyListItem {
  _id: string;
  basicDetails: AcademyBasicDetails;
}

interface AcademyListApiResponse {
  data?: {
    data?: AcademyListItem[];
    paginatedResults?: AcademyListItem[];
  } | AcademyListItem[];
}

/**
 * Fetch list of academies for scope ID selection.
 * Uses /api/v1/academy and normalizes to a flat array.
 */
export async function listAcademiesForScope(): Promise<AcademyListItem[]> {
  try {
    const response = await secureApi.get<AcademyListApiResponse>(
      "/api/v1/academy"
    );

    if (Array.isArray(response)) {
      return response as AcademyListItem[];
    }

    if (Array.isArray(response?.data)) {
      return response.data as AcademyListItem[];
    }

    // New API shape:
    // { data: { data: AcademyListItem[] } }
    if (Array.isArray(response?.data?.data)) {
      return response.data.data as AcademyListItem[];
    }

    if (Array.isArray(response?.data?.paginatedResults)) {
      return response.data.paginatedResults as AcademyListItem[];
    }

    return [];
  } catch (error) {
    console.error("Failed to fetch academies for scope IDs:", error);
    return [];
  }
}

// =========================
// Direct center listing (Access Management coach/coaching-center scopes)
// =========================

export interface CenterBasicDetailsV2 {
  _id: string;
  locationDisplay?: string;
  // Fallbacks (in case locationDisplay isn't present)
  location?: string;
}

export interface CenterListV2Item {
  _id?: string;
  basicDetails: CenterBasicDetailsV2;
}

interface AcademyListV2ApiResponse {
  data?: {
    paginatedResults?: CenterListV2Item[];
  };
}

export interface ListCentersV2Query {
  limit?: number;
  page?: number;
}

const centersV2Cache = new Map<string, CenterListV2Item[]>();
const centersV2InFlight = new Map<string, Promise<CenterListV2Item[]>>();

/**
 * Fetch direct centers for coach/coaching-center scope selection.
 * Uses: /api/v1/academy/list/website
 */
export async function listCentersV2ForScopeIds(
  search?: string,
  query?: ListCentersV2Query
): Promise<CenterListV2Item[]> {
  const trimmedSearch = search?.trim() ?? "";
  const pageSize = query?.limit ?? 200;
  const page = query?.page;
  const cacheKey = JSON.stringify({ search: trimmedSearch, pageSize, page });

  const cached = centersV2Cache.get(cacheKey);
  if (cached) return cached;

  const inFlight = centersV2InFlight.get(cacheKey);
  if (inFlight) return inFlight;

  try {
    const params: Record<string, string | number> = {};
    if (trimmedSearch) params.search = trimmedSearch;
    params.pageSize = pageSize;
    if (page != null) params.page = page;

    const request = (async () => {
      const response = await secureApi.get<AcademyListV2ApiResponse>(
        "/api/v1/academy/list/website",
        Object.keys(params).length ? { params } : undefined
      );

      const items = Array.isArray(response?.data?.paginatedResults)
        ? (response.data.paginatedResults as CenterListV2Item[])
        : [];

      centersV2Cache.set(cacheKey, items);
      return items;
    })();

    centersV2InFlight.set(cacheKey, request);
    return await request;
  } catch (error) {
    console.error("Failed to fetch centers (list/website) for scope IDs:", error);
    return [];
  } finally {
    centersV2InFlight.delete(cacheKey);
  }
}

// =========================
// Coaching centers by academy (for coaching-center scope type)
// =========================

export interface CoachingCenterBasicDetails {
  location: string;
}

export interface CoachingCenterItem {
  _id: string;
  academy: string;
  basicDetails: CoachingCenterBasicDetails;
}

interface CoachingCenterApiResponse {
  data?: {
    academyName?: string;
    coachingCenters?: CoachingCenterItem[];
  };
}

export async function listCoachingCentersForAcademy(
  academyId: string
): Promise<CoachingCenterItem[]> {
  if (!academyId) return [];

  try {
    const response = await secureApi.get<CoachingCenterApiResponse>(
      `/api/v1/academy/coaching-center/list/${academyId}`
    );

    if (Array.isArray(response)) {
      return response as CoachingCenterItem[];
    }

    if (Array.isArray(response?.data?.coachingCenters)) {
      return response.data!.coachingCenters as CoachingCenterItem[];
    }

    return [];
  } catch (error) {
    console.error("Failed to fetch coaching centers for scope IDs:", error);
    return [];
  }
}

// =========================
// Lightweight center names by academy (for promo targeting)
// =========================

export interface CenterNameItem {
  _id: string;
  basicDetails: {
    location: string;
  };
}

interface CenterNamesApiResponse {
  data?: CenterNameItem[] | null;
}

export async function listCenterNamesForAcademy(
  academyId: string
): Promise<CenterNameItem[]> {
  if (!academyId) return [];

  try {
    const response = await secureApi.get<CenterNamesApiResponse>(
      `/api/v1/academy/coaching-center/list-all-names/${academyId}`
    );

    if (Array.isArray(response)) {
      return response as CenterNameItem[];
    }

    if (Array.isArray(response?.data)) {
      return response.data as CenterNameItem[];
    }

    return [];
  } catch (error) {
    console.error("Failed to fetch center names for academy:", error);
    return [];
  }
}

